'use client'

import {
  type ChangeEvent,
  type FormEvent,
  forwardRef,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from 'react'

import {Button} from '@/components/Button'
import ContactUsTopic, { getTopic, type Topic } from './ContactUsTopic'

export default function ContactUsForm({
  topic: initialTopic,
}: { topic: string }) {
  const captchaRef = useRef<ReCAPTCHAVerifier>(null)
  const [
    { error, success, sending, topic, message, name, email, subject },
    dispatch,
  ] = useReducer(
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    (state: any, action: any): any => {
      switch (action.type) {
        case 'setSending':
          return {
            ...state,
            sending: true,
            success: undefined,
            error: undefined,
          }

        case 'setError':
          return {
            ...state,
            message: undefined,
            error: action.payload,
            sending: false,
          }

        case 'setSuccess':
          return {
            ...state,
            error: undefined,
            success: action.payload,
            sending: false,
          }

        case 'setFieldElement':
          return {
            ...state,
            [action.name]: action.value,
          }

        default:
          return state
      }
    },
    {
      sending: false,
      topic: getTopic(initialTopic),
    },
  )

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      dispatch({ type: 'setSending' })

      // TODO validate form fields
      if (!captchaRef || !captchaRef.current) {
        return
      }

      if (captchaRef.current === null) {
        return
      }

      const captchaResult = await captchaRef.current.verify()
      if (!captchaResult.success) {
        dispatch({
          type: 'setError',
          payload: 'Sorry, the capture was wrong. Please try again.',
        })
        return
      }

      try {
        const res = await fetch('/api/sayHi/send', {
          method: 'POST',
          body: JSON.stringify({
            topic: topic.id,
            message,
            name,
            email,
            subject,
          }),
          headers: {
            'Content-Type': 'application/json',
          },
        })

        const data = await res.json()

        if (res.status === 200) {
          dispatch({ type: 'setSuccess', payload: data.message })
        } else {
          dispatch({ type: 'setError', payload: data.message })
        }
      } catch (e) {
        const err = e as Error
        dispatch({ type: 'setError', payload: String(err.message) })
      }
    },
    [topic, message, name, email, subject],
  )

  useEffect(() => {
    dispatch({
      type: 'setFieldElement',
      name: 'topic',
      value: getTopic(initialTopic),
    })
  }, [initialTopic])

  function onSelectTopic(newTopic: Topic) {
    dispatch({ type: 'setFieldElement', name: 'topic', value: newTopic })
  }

  function generateFieldSetter<
    T extends HTMLInputElement | HTMLTextAreaElement,
  >(elementName: string) {
    return (event: ChangeEvent<T>) => {
      dispatch({
        type: 'setFieldElement',
        name: elementName,
        value: event.target.value,
      })
    }
  }

  return (
    <div className="isolate">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          Contact Us
        </h2>
        <p className="mt-2 text-lg leading-8 text-gray-600">Get in touch.</p>
      </div>

      <form
        onSubmit={onSubmit}
        method="POST"
        className="mx-auto mt-6 max-w-xl sm:mt-12"
      >
        <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
          {error && (
            <div className="mt-2 rounded-md bg-red-50 px-4 py-2 sm:col-span-2">
              <p className="text-sm font-medium text-red-900">{error}</p>
            </div>
          )}
          {success && (
            <div className="mt-2 rounded-md bg-green-50 px-4 py-2 sm:col-span-2">
              <p className="text-sm font-medium text-green-800">{success}</p>
            </div>
          )}

          <div className="sm:col-span-2">
            <ContactUsTopic selected={topic} onSelect={onSelectTopic} />
          </div>
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-semibold leading-6 text-gray-900"
            >
              Name
            </label>
            <div className="mt-2.5">
              <input
                type="text"
                name="name"
                id="name"
                required
                autoComplete="given-name"
                className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-300 sm:text-sm sm:leading-6"
                value={name}
                onChange={generateFieldSetter('name')}
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-semibold leading-6 text-gray-900"
            >
              Email
            </label>
            <div className="mt-2.5">
              <input
                type="email"
                name="email"
                id="email"
                required
                autoComplete="email"
                className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-300 sm:text-sm sm:leading-6"
                value={email}
                onChange={generateFieldSetter('email')}
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label
              htmlFor="subject"
              className="block text-sm font-semibold leading-6 text-gray-900"
            >
              Subject
            </label>
            <div className="mt-2.5">
              <input
                type="text"
                name="subject"
                id="subject"
                required
                className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-300 sm:text-sm sm:leading-6"
                value={subject}
                onChange={generateFieldSetter('subject')}
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label
              htmlFor="message"
              className="block text-sm font-semibold leading-6 text-gray-900"
            >
              Message
            </label>
            <div className="mt-2.5">
              <textarea
                name="message"
                id="message"
                rows={5}
                required
                className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-300 sm:text-sm sm:leading-6"
                value={message}
                onChange={generateFieldSetter('message')}
              />
            </div>
          </div>
        </div>

        <div className="mt-10 sm:col-span-2">
          <ReCAPTCHA
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string}
            ref={captchaRef}
          />
        </div>

        <div className="mt-10">
          <Button loading={sending} type="submit" color="violet">
            Talk to us
          </Button>
        </div>
      </form>
    </div>
  )
}

/**
 * Represents the window.grecaptcha object that https://www.google.com/recaptcha/api.js creates when it's included.
 */
interface GoogleRecaptcha {
  ready(callback: () => void): void
  execute(sitekey: string, options: { action?: string }): Promise<string>
}

type ReCAPTCHAResult = { success: boolean }
type ReCAPTCHAVerifier = { verify(): Promise<ReCAPTCHAResult> }

const ReCAPTCHA = forwardRef<
  ReCAPTCHAVerifier,
  { sitekey: string; action?: string }
>(function ReCAPTCHA({ sitekey, action = 'homepage' }, ref) {
  const handleLoaded = useCallback(async () => {
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const grecaptcha = (window as any).grecaptcha as GoogleRecaptcha

    grecaptcha.ready(() => {
      if (ref && typeof ref !== 'function') {
        ref.current = {
          async verify() {
            return grecaptcha.execute(sitekey, { action }).then(verifyToken)
          },
        }
      }
    })
  }, [action, ref, sitekey])

  useEffect(() => {
    // Add reCaptcha
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${sitekey}`
    script.addEventListener('load', handleLoaded)
    document.body.appendChild(script)
  }, [sitekey, handleLoaded])

  return (
    <div className="g-recaptcha" data-sitekey={sitekey} data-size="invisible" />
  )
})

async function verifyToken(token: string): Promise<ReCAPTCHAResult> {
  const res = await fetch('/api/sayHi/verify-token', {
    method: 'POST',
    body: JSON.stringify({ token }),
    headers: {
      'Content-Type': 'application/json',
    },
  })

  const { data } = await res.json()
  return data
}
